/**
 * Whether the SET of RDS VMs a Dedicated RDS server asks for can be created: the rows of the readiness panel (components/infrastructure/
 * RdsSetCheck.tsx) and their summary. Pure functions of what the page already knows: nothing here asks Azure or the service, and nothing
 * here decides what blocks Next. `validateInfrastructure` does that; these rows only SAY so, in words, for the whole set, using the same
 * messages (`networkProblems`, the size check's own message, the naming module's problem) so the two never disagree.
 */
import { rdsVmsText } from '@/data/sizeCatalog'
import type { NetworkChoice, VmSizeCheck, VmSizeChoice } from '@/types/infrastructure'
import { networkProblems, type ResolvedSqlServerName } from '@/utils/infraValidation'
import { wayOutText } from '@/utils/vmSizes'

/** `ok` passes, `warning` needs a look (or an answer), `problem` stops the step, `pending` is not known yet or waits for a choice. */
export type SetCheckStatus = 'ok' | 'warning' | 'problem' | 'pending'

export type SetCheckId = 'names' | 'size' | 'network'

export interface SetCheckRow {
  id: SetCheckId
  label: string
  status: SetCheckStatus
  /** What to tell the user, in a sentence. */
  text: string
  /** True when Next waits for this row to change: a problem, or an answer the user has still to give. */
  blocks: boolean
}

/** What each status is called in words, beside its icon: a status is never colour alone. */
export const SET_CHECK_STATUS_TEXT: Readonly<Record<SetCheckStatus, string>> = Object.freeze({
  ok: 'Passed',
  warning: 'Warning',
  problem: 'Problem',
  pending: 'Waiting',
})

const row = (id: SetCheckId, label: string, status: SetCheckStatus, text: string, blocks = false): SetCheckRow => ({ id, label, status, text, blocks })

const vcpusText = (count: number, check: Pick<VmSizeCheck, 'vcpus'>) => `${count} x ${check.vcpus} = ${count * check.vcpus} vCPUs`

// ─── Names ───────────────────────────────────────────────────────────────────

/** Where the next free number of the RDS series is: being worked out, could not be, or known (the names, or why they cannot be used). */
export type NamesState =
  | { state: 'waiting' }
  | { state: 'failed'; message: string }
  | { state: 'resolved'; resolved: ResolvedSqlServerName & { names?: string[] } }

/** The names of the whole set: every one of them, or the reason they cannot be. */
export function namesRow(input: NamesState): SetCheckRow {
  const label = 'Names'
  if (input.state === 'waiting') return row('names', label, 'pending', 'Working out the next free RDS server names.')
  if (input.state === 'failed') return row('names', label, 'problem', input.message, true)
  if (!input.resolved.ok) return row('names', label, 'problem', input.resolved.problem, true)
  return row('names', label, 'ok', (input.resolved.names ?? [input.resolved.name]).join(', '))
}

// ─── VM size and quota ───────────────────────────────────────────────────────

export interface SizeCheckInput {
  /** The RDS VM's answer of the size check (`useVmSizeCheck(count).rds`), or undefined when there is nothing to ask yet. */
  check: VmSizeCheck | undefined
  count: number
  /** True while the first answer is on its way. */
  loading: boolean
  /** True when the subscription's vCPU quota was read too. */
  quotaChecked: boolean
  /** Why Azure could not be asked, when it could not. */
  note: string
  /** What the user chose for a size that cannot be built as shown (null: nothing yet). */
  choice: VmSizeChoice | null
}

/** The RDS VM's size, and the vCPU quota the whole set needs. */
export function sizeRow(input: SizeCheckInput): SetCheckRow {
  const label = 'VM size and quota'
  const { check, count, choice } = input
  if (input.loading) return row('size', label, 'pending', 'Asking Azure whether the VM size can be built and how much vCPU quota is left.')
  if (!check) return row('size', label, 'pending', 'Choose the environment and region to check the VM size and the vCPU quota.')
  const needs = vcpusText(count, check)
  if (check.status === 'available') {
    return input.quotaChecked
      ? row('size', label, 'ok', `${count} x ${check.effective}, ${needs} of quota are available.`)
      : row('size', label, 'warning', `${count} x ${check.effective} can be built, but the subscription’s vCPU quota could not be read, so ${needs} are not confirmed.`)
  }
  if (check.status === 'substituted') {
    if (choice === 'use') return row('size', label, 'ok', `${count} x ${check.effective} will be built instead of ${check.requested}: ${needs} of quota are available.`)
    if (choice === 'wait') {
      return row('size', label, 'problem', `You chose to wait: nothing is built until ${count} x ${check.requested} can be. To continue, ${wayOutText(check)}.`, true)
    }
    return row('size', label, 'warning', `${check.message} The nearest series of the same size is offered: answer in the notice above, nothing is built until you do.`, true)
  }
  if (check.status === 'unavailable') return row('size', label, 'problem', check.message, true)
  return row('size', label, 'warning', `Could not check whether ${count} x ${check.requested} can be built with ${needs} of quota.${input.note ? ` ${input.note}` : ''} They are built as shown.`)
}

// ─── Network ─────────────────────────────────────────────────────────────────

/** The subnet of the RDS server: does it have a free IP for every VM of the set? The wording of a shortfall is `networkProblems`'. */
export function networkRow(network: NetworkChoice, count: number, networkLabel = 'RDS server network'): SetCheckRow {
  const label = 'Subnet'
  if (!network.vnet || !network.subnet) return row('network', label, 'pending', 'Choose a VNet and subnet.', true)
  const [problem] = networkProblems(network, count, networkLabel)
  if (problem) return row('network', label, 'problem', problem, true)
  const free = network.subnet.freeIps
  return row('network', label, 'ok', `Subnet ${network.subnet.name} has ${free} free IP${free === 1 ? '' : 's'}, ${count} ${count === 1 ? 'is' : 'are'} needed.`)
}

// ─── The whole set ───────────────────────────────────────────────────────────

/** What the rows add up to: issues to fix (alert), checks still to finish, checks that could not be confirmed, or all clear (the last three are a status). */
export interface SetCheckSummary {
  kind: 'issues' | 'waiting' | 'unconfirmed' | 'passed'
  text: string
  /** The rows that stop the step: problems, and the answers the user has still to give. */
  issues: number
}

const plural = (count: number, one: string, many: string) => (count === 1 ? one : many)

export function summariseSetCheck(rows: readonly SetCheckRow[], count: number): SetCheckSummary {
  const issues = rows.filter((item) => item.status === 'problem' || (item.status === 'warning' && item.blocks)).length
  const waiting = rows.filter((item) => item.status === 'pending').length
  const unconfirmed = rows.filter((item) => item.status === 'warning' && !item.blocks).length
  const set = rdsVmsText(count)
  if (issues > 0) return { kind: 'issues', issues, text: `${issues} ${plural(issues, 'issue', 'issues')} to fix before continuing` }
  if (waiting > 0) return { kind: 'waiting', issues, text: `Not all checks have finished for ${set}.` }
  if (unconfirmed > 0) {
    return { kind: 'unconfirmed', issues, text: `Nothing blocks continuing for ${set}, but ${unconfirmed} ${plural(unconfirmed, 'check is', 'checks are')} not confirmed.` }
  }
  return { kind: 'passed', issues, text: `All checks passed for ${set}` }
}

/** "Can this RDS VM be created?" or "Can these 3 RDS VMs be created?" */
export const setCheckTitle = (count: number): string => (count === 1 ? 'Can this RDS VM be created?' : `Can these ${count} RDS VMs be created?`)
